<template>
    <div class="modern-home">
        <section class="modern-home__welcome">
            <div>
                <div class="modern-home__eyebrow">РАБОЧИЙ СТОЛ</div>
                <h1>{{ greeting }}, {{ firstName }}</h1>
                <p>Главное на сегодня — без лишних переходов по меню.</p>
            </div>
        </section>

        <section class="modern-home__hero">
            <div class="modern-home__hero-icon">
                <i :class="primaryAction.icon" aria-hidden="true"></i>
            </div>
            <div class="modern-home__hero-copy">
                <div class="modern-home__eyebrow">{{ primaryAction.eyebrow }}</div>
                <h2>{{ primaryAction.title }}</h2>
                <p>{{ primaryAction.context }}</p>
            </div>
            <b-button
                variant="primary"
                size="lg"
                class="modern-home__hero-button"
                @click="openPrimaryAction"
            >
                {{ primaryAction.button }}
                <i class="fas fa-arrow-right ml-2" aria-hidden="true"></i>
            </b-button>
        </section>

        <InstallPromotionCard
            v-if="installPromotionVisible"
            class="modern-home__install-card"
            preview-size="compact"
            @install="openInstallPage"
            @installed="confirmAlreadyInstalled"
            @snooze="snoozeInstallation"
        />

        <div class="modern-home__columns">
            <section class="modern-home__panel">
                <div class="modern-home__section-head">
                    <div>
                        <div class="modern-home__eyebrow">СЕЙЧАС</div>
                        <h2>Требует внимания</h2>
                    </div>
                    <span class="modern-home__total-badge">{{ attentionTotal }}</span>
                </div>

                <div class="modern-home__primary-queues" role="group" aria-label="Задачи и заявки">
                    <button
                        type="button"
                        class="modern-home__primary-queue"
                        :class="{'modern-home__primary-queue--pending': taskAttention.total > 0,
                                 'modern-home__primary-queue--overdue': taskAttention.overdue > 0}"
                        :aria-label="`Задачи. ${taskAttentionText}. ${taskAttentionDetails}`"
                        @click="openAttentionTasks"
                    >
                        <span class="modern-home__queue-icon">
                            <i class="fas fa-bell" aria-hidden="true"></i>
                        </span>
                        <span class="modern-home__queue-copy">
                            <strong>Задачи</strong>
                            <small>{{ taskAttentionDetails }}</small>
                        </span>
                        <span class="modern-home__primary-count">
                            <strong>{{ taskAttention.total || 0 }}</strong>
                        </span>
                        <i class="fas fa-chevron-right modern-home__chevron" aria-hidden="true"></i>
                    </button>

                    <button
                        type="button"
                        class="modern-home__primary-queue"
                        :class="{'modern-home__primary-queue--pending': requestAttention > 0}"
                        :aria-label="`Заявки. ${requestAttentionText}`"
                        @click="openAttentionRequests"
                    >
                        <span class="modern-home__queue-icon">
                            <i class="fas fa-clipboard-list" aria-hidden="true"></i>
                        </span>
                        <span class="modern-home__queue-copy">
                            <strong>Заявки</strong>
                            <small>{{ requestAttention ? 'Ждут вашего участия' : 'Нет заявок, требующих участия' }}</small>
                        </span>
                        <span class="modern-home__primary-count">
                            <strong>{{ requestAttention }}</strong>
                        </span>
                        <i class="fas fa-chevron-right modern-home__chevron" aria-hidden="true"></i>
                    </button>
                </div>

                <div v-if="homeAttentionGroups.length" class="modern-home__secondary-queues">
                    <div v-for="group in homeAttentionGroups" :key="group.key">
                        <button
                            type="button"
                            class="modern-home__queue"
                            :aria-expanded="attentionExpanded[group.key] ? 'true' : 'false'"
                            :aria-controls="`home-attention-${group.key}`"
                            @click="attentionExpanded[group.key] = !attentionExpanded[group.key]"
                        >
                            <span class="modern-home__queue-icon modern-home__queue-icon--tasks">
                                <i :class="group.icon" aria-hidden="true"></i>
                            </span>
                            <span class="modern-home__queue-copy">
                                <strong>{{ group.title }}</strong>
                                <small>{{ group.hint }}</small>
                            </span>
                            <span class="modern-home__queue-count">{{ group.items.length }}</span>
                            <i :class="['fas', attentionExpanded[group.key] ? 'fa-chevron-down' : 'fa-chevron-right', 'modern-home__chevron']" aria-hidden="true"></i>
                        </button>
                        <b-collapse :id="`home-attention-${group.key}`" v-model="attentionExpanded[group.key]">
                            <div v-if="group.key === 'shiftDrafts'" class="modern-home__draft-actions">
                                <small class="text-muted">Сначала новые смены</small>
                                <b-button size="sm" variant="outline-danger" :disabled="clearingShiftDrafts"
                                          @click="clearShiftDrafts">
                                    <i class="fas fa-trash-alt mr-1" aria-hidden="true"></i>
                                    {{ clearingShiftDrafts ? 'Очистка…' : 'Очистить черновики' }}
                                </b-button>
                            </div>
                            <ul class="modern-home__returned-list">
                                <li v-for="item in group.items" :key="item.id">
                                    <router-link
                                        class="modern-home__returned-link"
                                        :to="item.to"
                                    >
                                        <strong>{{ item.label }}</strong>
                                        <span>{{ item.context }}</span>
                                        <small v-if="item.reason" :title="item.reason">{{ item.reason }}</small>
                                    </router-link>
                                </li>
                            </ul>
                        </b-collapse>
                    </div>
                </div>

                <div v-if="attentionTotal === 0" class="modern-home__calm">
                    <i class="fas fa-check-circle" aria-hidden="true"></i>
                    Сейчас ничего не требует внимания
                </div>
            </section>

            <section class="modern-home__panel">
                <div class="modern-home__section-head">
                    <div>
                        <div class="modern-home__eyebrow">ИСТОРИЯ НА ЭТОМ УСТРОЙСТВЕ</div>
                        <h2>Продолжить работу</h2>
                    </div>
                </div>

                <button
                    v-for="item in visibleRecentItems"
                    :key="item.id"
                    type="button"
                    class="modern-home__recent"
                    @click="openRecent(item)"
                >
                    <span class="modern-home__recent-icon">
                        <i :class="item.icon" aria-hidden="true"></i>
                    </span>
                    <span>
                        <strong>{{ item.label }}</strong>
                        <small>
                            <template v-if="item.context_label">{{ item.context_label }} · </template>
                            {{ formatVisitedAt(item.visited_at) }}
                        </small>
                    </span>
                    <i class="fas fa-chevron-right modern-home__chevron ml-auto" aria-hidden="true"></i>
                </button>

                <div v-if="!visibleRecentItems.length" class="modern-home__empty">
                    Откройте доску сотрудников, план, табели или рабочую карточку — последние посещения появятся здесь.
                </div>
            </section>
        </div>
    </div>
</template>

<script>
import {getAvailableWorkspaces, getWorkspaceByKey} from '@/utils/workspaceRegistry';
import {getRecentWork} from '@/utils/recentWork';
import InstallPromotionCard from '@/components/InstallPromotionCard.vue';
import {
    INSTALLATION_STATE_CHANGED_EVENT,
    markAppAlreadyInstalled,
    markInstallPromotionSessionDismissed,
    openInstallUrlExternally,
    shouldShowInstallPromotion,
    snoozeInstallPromotion
} from '@/utils/appInstallation';

const ACTION_COPY = {
    shift_report: {
        eyebrow: 'СМЕННЫЙ ОТЧЁТ',
        title: 'Сдать отчёт по смене',
        button: 'Открыть отчёт'
    },
    objects: {eyebrow: 'ОБЪЕКТЫ', title: 'Открыть строительные объекты', button: 'К объектам'},
    reports: {eyebrow: 'ОТЧЁТЫ', title: 'Посмотреть сменные отчёты', button: 'К отчётам'},
    mechanic_assets: {eyebrow: 'ТЕХНИКА', title: 'Открыть справочник техники', button: 'К технике'},
    material_using: {eyebrow: 'МАТЕРИАЛЫ', title: 'Проверить использование материалов', button: 'Открыть'},
    materials: {eyebrow: 'МАТЕРИАЛЫ', title: 'Открыть справочник материалов', button: 'Открыть'},
    fuel_cards: {eyebrow: 'ТОПЛИВО', title: 'Открыть топливные карты', button: 'Открыть'},
    fuel_limits: {eyebrow: 'ТОПЛИВО', title: 'Проверить лимиты топлива', button: 'Открыть'},
    used_fuels: {eyebrow: 'ТОПЛИВО', title: 'Посмотреть расход топлива', button: 'Открыть'},
    employees: {eyebrow: 'СОТРУДНИКИ', title: 'Открыть справочник сотрудников', button: 'К сотрудникам'},
    gallery: {eyebrow: 'ГАЛЕРЕЯ', title: 'Посмотреть свежие фотографии', button: 'К фотографиям'},
    charts: {eyebrow: 'АНАЛИТИКА', title: 'Открыть рабочие графики', button: 'К графикам'},
    completed_works: {eyebrow: 'РАБОТЫ', title: 'Посмотреть выполненные работы', button: 'Открыть'},
    work_groups: {eyebrow: 'РАБОТЫ', title: 'Открыть группы работ', button: 'Открыть'},
    work_types: {eyebrow: 'РАБОТЫ', title: 'Открыть виды работ', button: 'Открыть'}
};

function pluralizeRu(value, one, few, many) {
    const count = Math.abs(Number(value) || 0);
    const mod100 = count % 100;
    const mod10 = count % 10;
    if (mod100 >= 11 && mod100 <= 14) return many;
    if (mod10 === 1) return one;
    if (mod10 >= 2 && mod10 <= 4) return few;
    return many;
}

function greetingByHour(hour) {
    if (hour < 5 || hour >= 23) return 'Доброй ночи';
    if (hour < 12) return 'Доброе утро';
    if (hour < 18) return 'Добрый день';
    return 'Добрый вечер';
}

export default {
    name: 'home',
    components: {
        InstallPromotionCard
    },
    data() {
        return {
            recentItems: [],
            attentionExpanded: {returned: false, review: false, arrivals: false, shiftDrafts: false},
            clearingShiftDrafts: false,
            installPromotionVisible: false,
            installationCheckId: 0,
            localHour: new Date().getHours(),
            greetingTimer: null
        };
    },
    computed: {
        currentUser() {
            return this.$store.getters.currentUser;
        },
        firstName() {
            return (this.currentUser && this.currentUser.firstName) || 'коллега';
        },
        greeting() {
            return greetingByHour(this.localHour);
        },
        navigationReady() {
            return this.$store.getters.navigationReady;
        },
        isTelegramWebApp() {
            return this.$store.getters.isTelegramWebApps;
        },
        navigation() {
            return this.$store.state.navigationExperience || {};
        },
        availableWorkspaces() {
            return getAvailableWorkspaces(this);
        },
        visibleRecentItems() {
            const canOpenWorkforce = this.availableWorkspaces.some(item => item.key === 'employees_workforce');
            return this.recentItems.filter(item => (
                !['workforce_board', 'workforce_plan'].includes(item.type) || canOpenWorkforce
            ));
        },
        effectiveWorkspace() {
            const availableKeys = new Set(this.availableWorkspaces.map(item => item.key));
            const preferred = this.navigation.preferred_workspace_key;
            if (preferred && availableKeys.has(preferred)) {
                return getWorkspaceByKey(preferred);
            }
            const departmentDefaults = Array.isArray(this.navigation.department_default_workspace_keys)
                ? this.navigation.department_default_workspace_keys
                : [];
            const availableDefaults = departmentDefaults.filter(key => availableKeys.has(key));
            if (availableDefaults.length === 1) {
                return getWorkspaceByKey(availableDefaults[0]);
            }
            if (availableDefaults.length > 1) {
                return null;
            }
            return this.availableWorkspaces[0] || null;
        },
        shouldChooseWorkspace() {
            if (this.navigation.preferred_workspace_key) return false;
            const availableKeys = new Set(this.availableWorkspaces.map(item => item.key));
            const defaults = Array.isArray(this.navigation.department_default_workspace_keys)
                ? this.navigation.department_default_workspace_keys
                : [];
            return defaults.filter(key => availableKeys.has(key)).length > 1;
        },
        homeSummary() {
            return this.navigation.home || {};
        },
        shiftDraft() {
            return this.homeSummary.shift_report && this.homeSummary.shift_report.draft
                ? this.homeSummary.shift_report.draft
                : null;
        },
        primaryAction() {
            if (this.shouldChooseWorkspace) {
                return {
                    eyebrow: 'ПЕРСОНАЛЬНАЯ НАВИГАЦИЯ',
                    title: 'Выберите свой главный раздел',
                    button: 'Выбрать раздел',
                    context: 'Для ваших отделов заданы разные рабочие пространства',
                    icon: 'fas fa-star'
                };
            }
            const workspace = this.effectiveWorkspace;
            const key = workspace ? workspace.key : null;
            const fallback = {
                eyebrow: 'АРГУС',
                title: 'Открыть рабочий раздел',
                button: 'Открыть'
            };
            const copy = ACTION_COPY[key] || (key && key.indexOf('requests_') === 0
                ? {eyebrow: 'РАБОЧАЯ ОЧЕРЕДЬ', title: workspace.label, button: 'Открыть очередь'}
                : fallback);
            let context = workspace
                ? `Ваш быстрый раздел: ${workspace.label}`
                : 'Выберите рабочий раздел в профиле';
            let title = copy.title;
            let button = copy.button;
            if (key === 'shift_report' && this.shiftDraft) {
                title = 'Продолжить отчёт по смене';
                button = 'Продолжить';
                const parts = [
                    this.shiftDraft.object_name,
                    this.shiftDraft.start_date
                        ? this.$moment(this.shiftDraft.start_date).format('DD.MM.YYYY')
                        : null
                ].filter(Boolean);
                context = parts.length ? parts.join(' · ') : 'Сохранённый черновик отчёта';
            }
            return {
                ...copy,
                title,
                button,
                context,
                icon: workspace ? workspace.icon : 'fas fa-compass'
            };
        },
        taskAttention() {
            return (this.navigation.attention && this.navigation.attention.tasks) || {
                total: 0,
                overdue: 0,
                upcoming: 0,
                control_due: 0
            };
        },
        requestAttention() {
            return Number(this.navigation.attention && this.navigation.attention.requests) || 0;
        },
        returnedTimesheets() {
            return this.homeSummary.timesheets && Array.isArray(this.homeSummary.timesheets.returned)
                ? this.homeSummary.timesheets.returned : [];
        },
        returnedTimesheetsText() {
            const count = this.returnedTimesheets.length;
            const noun = pluralizeRu(count, 'табель', 'табеля', 'табелей');
            return `${count} ${noun} на исправление`;
        },
        reviewTimesheets() {
            return this.homeSummary.timesheets && Array.isArray(this.homeSummary.timesheets.review)
                ? this.homeSummary.timesheets.review : [];
        },
        pendingArrivals() {
            return Array.isArray(this.homeSummary.arrivals) ? this.homeSummary.arrivals : [];
        },
        overdueShiftDrafts() {
            return this.homeSummary.shift_report && Array.isArray(this.homeSummary.shift_report.overdue_drafts)
                ? this.homeSummary.shift_report.overdue_drafts : [];
        },
        homeAttentionGroups() {
            const sheetItem = (item, review) => ({id: item.id,
                label: [item.object_name, item.name].filter(Boolean).join(' · '),
                context: this.formatTimesheetMonth(item.month),
                reason: review ? '' : item.return_reason || 'Требуются исправления',
                to: {name: 'timesheet', params: {timesheetId: String(item.id)},
                    query: {month: item.month, ...(review ? {from: 'review'} : {})}}});
            return [
                {key: 'returned', title: this.returnedTimesheetsText, hint: 'Исправьте замечания и подайте повторно',
                    icon: 'fas fa-undo', items: this.returnedTimesheets.map(item => sheetItem(item, false))},
                {key: 'review', title: 'Табели ожидают проверки', hint: 'Проверьте и утвердите или верните с замечаниями',
                    icon: 'fas fa-calendar-check', items: this.reviewTimesheets.map(item => sheetItem(item, true))},
                {key: 'arrivals', title: 'Подтвердить прибытие сотрудников', hint: 'Плановая дата уже наступила',
                    icon: 'fas fa-user-check', items: this.pendingArrivals.map(item => ({id: item.key,
                        label: item.employee_name,
                        context: `${item.object_name} · ${this.$moment(item.planned_for).format('DD.MM.YYYY')}`,
                        reason: item.target_type === 'object_group' ? 'Прибытие в группу объектов' : '',
                        to: item.timesheet_id
                            ? {name: 'timesheet', params: {timesheetId: String(item.timesheet_id)}, query: {month: item.month, attention: 'arrivals', arrival_key: item.key}}
                            : {name: 'timesheets', query: {month: item.month, object_id: String(item.building_id), attention: 'arrivals', arrival_key: item.key}}}))},
                {key: 'shiftDrafts', title: 'Не завершены отчёты по смене', hint: 'Смена закончилась — продолжите сохранённый отчёт',
                    icon: 'fas fa-clipboard-check', items: this.overdueShiftDrafts.map(item => ({id: item.id,
                        label: item.object_name, context: `${this.$moment(item.start_date).format('DD.MM.YYYY')} · ${item.start_time}`,
                        to: {path: '/shift-report-master', query: {process_id: item.building_process_id,
                            start_date: item.start_date, resume: 'true'}}}))}
            ].filter(group => group.items.length);
        },
        attentionTotal() {
            return (Number(this.taskAttention.total) || 0) + this.requestAttention + this.returnedTimesheets.length
                + this.reviewTimesheets.length + this.pendingArrivals.length + this.overdueShiftDrafts.length;
        },
        taskAttentionText() {
            const count = Number(this.taskAttention.total) || 0;
            if (!count) return 'По задачам всё спокойно';
            const noun = pluralizeRu(count, 'задача', 'задачи', 'задач');
            const verb = count % 10 === 1 && count % 100 !== 11 ? 'требует' : 'требуют';
            return `${count} ${noun} ${verb} внимания`;
        },
        taskAttentionDetails() {
            const parts = [];
            if (this.taskAttention.overdue) parts.push(`просрочено: ${this.taskAttention.overdue}`);
            if (this.taskAttention.upcoming) {
                parts.push(`в ближайшие 7 дней: ${this.taskAttention.upcoming}`);
            }
            if (this.taskAttention.control_due) parts.push(`контроль: ${this.taskAttention.control_due}`);
            return parts.length ? parts.join(' · ') : 'Нет просрочек и ближайших сроков';
        },
        requestAttentionText() {
            if (!this.requestAttention) return 'Нет заявок, требующих участия';
            const noun = pluralizeRu(this.requestAttention, 'заявка', 'заявки', 'заявок');
            const verb = this.requestAttention % 10 === 1 && this.requestAttention % 100 !== 11
                ? 'требует'
                : 'требуют';
            return `${this.requestAttention} ${noun} ${verb} вашего участия`;
        }
    },
    watch: {
        navigationReady(value) {
            if (value) {
                this.loadModernHome();
                this.refreshInstallPromotion();
            } else {
                this.installPromotionVisible = false;
            }
        }
    },
    mounted() {
        this.greetingTimer = window.setInterval(this.refreshGreetingHour, 60000);
        window.addEventListener(
            INSTALLATION_STATE_CHANGED_EVENT,
            this.refreshInstallPromotion
        );
        if (this.navigationReady) {
            this.loadModernHome();
            this.refreshInstallPromotion();
        }
    },
    beforeDestroy() {
        if (this.greetingTimer) {
            window.clearInterval(this.greetingTimer);
            this.greetingTimer = null;
        }
        window.removeEventListener(
            INSTALLATION_STATE_CHANGED_EVENT,
            this.refreshInstallPromotion
        );
    },
    methods: {
        async clearShiftDrafts() {
            if (this.clearingShiftDrafts || !this.overdueShiftDrafts.length) return;
            const drafts = this.overdueShiftDrafts.map(({id, updated_at}) => ({id, updated_at}));
            this.clearingShiftDrafts = true;
            try {
                const confirmed = await this.$bvModal.msgBoxConfirm(
                    `Будут безвозвратно удалены ваши незавершённые черновики из этого списка (${drafts.length}). ` +
                    'Все введённые в них данные будут потеряны. Восстановить их нельзя. ' +
                    'Уже сданные отчёты и чужие черновики останутся. Продолжить?',
                    {title: 'Удалить незавершённые черновики?', okTitle: 'Удалить черновики',
                        cancelTitle: 'Отмена', okVariant: 'danger', centered: true, autoFocusButton: 'cancel'}
                );
                if (!confirmed) return;
                const result = await this.$store.dispatch('navigation_clearShiftReportDrafts', drafts);
                await this.$store.dispatch('navigation_loadHomeSummary');
                this.$bvToast.toast(`Удалено черновиков: ${result.deleted_count}.` +
                    (result.skipped_count ? ` Пропущено: ${result.skipped_count} — они изменились или больше не входят в список.` : ''),
                {title: 'Очистка черновиков', variant: result.skipped_count ? 'warning' : 'success', solid: true});
            } catch (error) {
                this.$bvToast.toast(error.response && error.response.data && error.response.data.msg ||
                    'Не удалось очистить черновики. Обновите страницу и попробуйте снова.',
                {title: 'Очистка черновиков', variant: 'danger', solid: true});
            } finally {
                this.clearingShiftDrafts = false;
            }
        },
        refreshGreetingHour() {
            const localHour = new Date().getHours();
            if (localHour !== this.localHour) {
                this.localHour = localHour;
            }
        },
        async loadModernHome() {
            this.recentItems = getRecentWork(this.currentUser);
            await this.$store.dispatch('navigation_loadHomeSummary');
        },
        workspaceLocation(workspace) {
            if (!workspace) return '/more';
            return workspace.to || workspace.path || '/more';
        },
        openPrimaryAction() {
            if (this.shouldChooseWorkspace) {
                this.$router.push('/profile');
                return;
            }
            const workspace = this.effectiveWorkspace;
            const location = this.workspaceLocation(workspace);
            if (workspace && workspace.key === 'shift_report' && this.shiftDraft) {
                this.$router.push({
                    path: '/shift-report-master',
                    query: {
                        process_id: this.shiftDraft.building_process_id,
                        start_date: this.shiftDraft.start_date || undefined,
                        resume: 'true'
                    }
                });
                return;
            }
            this.$router.push(location);
        },
        openAttentionTasks() {
            this.$router.push({path: '/tasks', query: {attention: 'true'}});
        },
        openAttentionRequests() {
            // Выбираем вкладку до перехода: список заявок хранит активную вкладку
            // в Vuex и может восстановить ранее открытый раздел при монтировании.
            this.$store.commit('requests_setSelectedTab', 1);
            this.$store.commit('requests_setListPage', 1);
            this.$router.push({
                path: '/requests',
                query: {tab: 'requires_action'}
            });
        },
        openRecent(item) {
            if (item && item.path) this.$router.push(item.path);
        },
        async refreshInstallPromotion() {
            if (this.isTelegramWebApp) {
                this.installPromotionVisible = false;
                return;
            }
            const checkId = ++this.installationCheckId;
            const shouldShow = await shouldShowInstallPromotion();
            if (checkId !== this.installationCheckId) {
                return;
            }
            this.installPromotionVisible = Boolean(
                this.navigationReady
                && !this.isTelegramWebApp
                && shouldShow
            );
        },
        async openInstallPage() {
            if (this.isTelegramWebApp) {
                await openInstallUrlExternally();
                return;
            }
            markInstallPromotionSessionDismissed();
            this.$router.push('/install');
        },
        confirmAlreadyInstalled() {
            markAppAlreadyInstalled();
            this.installPromotionVisible = false;
        },
        snoozeInstallation() {
            snoozeInstallPromotion();
            this.installPromotionVisible = false;
        },
        formatVisitedAt(value) {
            if (!value) return 'Недавно';
            return this.$moment(value).fromNow();
        },
        formatTimesheetMonth(value) {
            return this.$moment(value, 'YYYY-MM', true).format('MMMM YYYY');
        }
    }
};
</script>

<style scoped>
.modern-home {
    max-width: 1180px;
    margin: 0 auto;
    padding: 32px 28px 48px;
    color: #172033;
}

.modern-home__welcome {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 24px;
    margin-bottom: 28px;
}

.modern-home__welcome h1 {
    margin: 4px 0 6px;
    font-size: clamp(30px, 4vw, 46px);
    line-height: 1.08;
    letter-spacing: -.035em;
    font-weight: 800;
}

.modern-home__welcome p,
.modern-home__hero p {
    margin: 0;
    color: #69758a;
    font-size: 16px;
}

.modern-home__eyebrow {
    color: #66758f;
    font-size: 11px;
    line-height: 1.3;
    letter-spacing: .12em;
    font-weight: 800;
}

.modern-home__hero {
    display: grid;
    grid-template-columns: 72px minmax(0, 1fr) auto;
    align-items: center;
    gap: 22px;
    padding: 28px;
    margin-bottom: 24px;
    border: 1px solid rgba(28, 100, 242, .14);
    border-radius: 24px;
    background:
        radial-gradient(circle at 85% 15%, rgba(76, 140, 255, .18), transparent 34%),
        linear-gradient(135deg, #f8fbff 0%, #eef5ff 100%);
    box-shadow: 0 16px 45px rgba(32, 74, 140, .10);
}

.modern-home__hero-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 72px;
    height: 72px;
    border-radius: 20px;
    background: #1d6ff2;
    color: #fff;
    font-size: 28px;
    box-shadow: 0 12px 24px rgba(29, 111, 242, .24);
}

.modern-home__hero h2 {
    margin: 4px 0 6px;
    color: #113c7d;
    font-size: clamp(23px, 3vw, 32px);
    font-weight: 800;
    letter-spacing: -.02em;
}

.modern-home__hero-button {
    min-height: 50px;
    padding: 0 22px;
    border: 0;
    border-radius: 14px;
    font-weight: 800;
    box-shadow: 0 10px 22px rgba(29, 111, 242, .20);
}

.modern-home__install-card {
    margin-bottom: 24px;
}

.modern-home__columns {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: 24px;
}

.modern-home__panel {
    min-width: 0;
    padding: 24px;
    border: 1px solid #e7ebf1;
    border-radius: 22px;
    background: #fff;
    box-shadow: 0 12px 35px rgba(24, 39, 75, .06);
}

.modern-home__primary-queues {
    display: grid;
    gap: 8px;
}

.modern-home__secondary-queues {
    margin-top: 16px;
    padding-top: 8px;
    border-top: 1px solid #e7ebf1;
}

.modern-home__secondary-queues > div:first-child > .modern-home__queue {
    border-top: 0;
}

.modern-home__primary-queue {
    display: flex;
    align-items: center;
    min-width: 0;
    min-height: 68px;
    padding: 12px 8px;
    border: 0;
    border-radius: 12px;
    background: #f7f9fc;
    color: #202a3a;
    text-align: left;
}

.modern-home__primary-queue:focus {
    outline: 2px solid #1b5fc2;
    outline-offset: 3px;
}

.modern-home__primary-queue .modern-home__queue-copy > strong {
    font-size: 16px;
}

.modern-home__primary-queue .modern-home__queue-copy > small {
    color: #69758a;
    white-space: normal;
    overflow-wrap: anywhere;
}

.modern-home__primary-count {
    flex: 0 0 auto;
    margin-left: 12px;
    color: #69758a;
    text-align: right;
    font-variant-numeric: tabular-nums;
}

.modern-home__primary-count > strong {
    display: block;
    font-size: 20px;
    line-height: 1.1;
}

.modern-home__primary-queue--pending .modern-home__primary-count {
    color: #1b5fc2;
}

.modern-home__primary-queue--pending .modern-home__queue-icon {
    background: #e9f2ff;
    color: #1559c4;
}

.modern-home__primary-queue--overdue .modern-home__primary-count {
    color: #b42332;
}

@media (hover: hover) and (pointer: fine) {
    .modern-home__primary-queue:hover {
        background: #eef2f7;
    }
}

.modern-home__section-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 14px;
}

.modern-home__section-head h2 {
    margin: 3px 0 0;
    font-size: 22px;
    font-weight: 800;
}

.modern-home__total-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 34px;
    height: 34px;
    padding: 0 9px;
    border-radius: 999px;
    background: #172033;
    color: #fff;
    font-weight: 800;
}

.modern-home__queue,
.modern-home__recent {
    display: flex;
    align-items: center;
    width: 100%;
    padding: 13px 8px;
    border: 0;
    border-top: 1px solid #edf0f4;
    background: transparent;
    color: #202a3a;
    text-align: left;
}

.modern-home__queue:hover,
.modern-home__recent:hover {
    border-radius: 14px;
    background: #f7f9fc;
}

.modern-home__queue-icon,
.modern-home__recent-icon {
    display: inline-flex;
    flex: 0 0 auto;
    align-items: center;
    justify-content: center;
    width: 42px;
    height: 42px;
    margin-right: 12px;
    border-radius: 13px;
    background: #eef2f7;
    color: #39475c;
}

.modern-home__queue-icon--tasks {
    background: #fff4dc;
    color: #a86400;
}

.modern-home__queue-copy,
.modern-home__recent > span:nth-child(2) {
    display: flex;
    min-width: 0;
    flex: 1 1 auto;
    flex-direction: column;
}

.modern-home__recent strong {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.modern-home__queue-copy small,
.modern-home__recent small {
    margin-top: 3px;
    color: #7a8597;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.modern-home__queue-count {
    min-width: 26px;
    margin-left: 10px;
    color: #1b5fc2;
    font-size: 16px;
    font-weight: 800;
    text-align: center;
}

.modern-home__draft-actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 4px 8px 12px;
}

.modern-home__returned-list {
    max-height: 320px;
    margin: 0 0 8px;
    padding: 0;
    overflow-y: auto;
    list-style: none;
}

.modern-home__returned-link {
    display: flex;
    flex-direction: column;
    gap: 3px;
    padding: 10px 12px;
    border-top: 1px solid #edf0f4;
    color: #202a3a;
}

.modern-home__returned-link:hover,
.modern-home__returned-link:focus {
    background: #f7f9fc;
    text-decoration: none;
}

.modern-home__returned-link > strong {
    overflow-wrap: anywhere;
}

.modern-home__returned-link > span,
.modern-home__returned-link > small {
    color: #69758a;
    font-size: 12px;
}

.modern-home__returned-link > small {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.modern-home__chevron {
    margin-left: 10px;
    color: #a6afbc;
    font-size: 12px;
}

.modern-home__calm,
.modern-home__empty {
    margin-top: 12px;
    padding: 14px;
    border-radius: 14px;
    background: #f4faf6;
    color: #34724a;
}

.modern-home__calm i {
    margin-right: 8px;
}

.modern-home__empty {
    background: #f7f9fc;
    color: #748095;
    line-height: 1.55;
}

@media (max-width: 767.98px) {
    .modern-home {
        padding: 22px 14px 28px;
    }

    .modern-home__welcome {
        align-items: flex-start;
        flex-direction: column;
        margin-bottom: 20px;
    }

    .modern-home__welcome h1 {
        font-size: 32px;
    }

    .modern-home__hero {
        grid-template-columns: 56px minmax(0, 1fr);
        gap: 14px;
        padding: 20px;
        border-radius: 20px;
    }

    .modern-home__hero-icon {
        width: 56px;
        height: 56px;
        border-radius: 16px;
        font-size: 22px;
    }

    .modern-home__hero h2 {
        font-size: 23px;
    }

    .modern-home__hero-button {
        grid-column: 1 / -1;
        width: 100%;
    }

    .modern-home__install-card {
        margin-bottom: 18px;
    }

    .modern-home__columns {
        grid-template-columns: 1fr;
        gap: 16px;
    }

    .modern-home__panel {
        padding: 18px 14px;
        border-radius: 18px;
    }

    .modern-home__queue {
        padding-left: 4px;
        padding-right: 4px;
    }

    .modern-home__queue-copy small {
        white-space: normal;
    }
}
</style>
